import { createFileRoute } from '@tanstack/react-router'

import type { User } from '@/api/admin-types'
import { UserRole } from '@/api/admin-types'
import { useUpdateUser } from '@/api/api-mutations'
import { useGetMe } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/users/$userUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.Users.EditTitle()
	}
})

function RouteComponent() {
	const { userUuid } = Route.useParams()
	return (
		<RouteModal asDrawer requiresData loader={async api => await api.getUser({ path: { userUuid } })}>
			{data => <EditUserForm user={data} />}
		</RouteModal>
	)
}

function EditUserForm({ user }: { user: User }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { userUuid } = Route.useParams()
	const me = useGetMe()
	const isSelf = me.data?.uuid === userUuid

	const updateUser = useUpdateUser()

	const form = useAppForm({
		defaultValues: {
			firstName: user.firstName || '',
			lastName: user.lastName || '',
			role: user.role
		},
		onSubmit: async ({ value }) => {
			const result = await updateUser({
				path: { userUuid },
				body: {
					firstName: value.firstName,
					lastName: value.lastName,
					...(isSelf ? {} : { role: value.role })
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.Users.EditSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.Users.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='firstName'
							children={field => (
								<field.TextField autoFocus label={L.Fields.FirstName()} placeholder='John' />
							)}
						/>
						<form.AppField
							name='lastName'
							children={field => <field.TextField label={L.Fields.LastName()} placeholder='Doe' />}
						/>
					</FormFieldGroup>
					{!isSelf ? (
						<FormFieldGroup>
							<form.AppField
								name='role'
								children={field => (
									<field.SelectField
										label={L.Fields.Role()}
										options={[
											{ value: UserRole.User, label: L.Enum.UserRole('USER') },
											{ value: UserRole.Admin, label: L.Enum.UserRole('ADMIN') },
											{ value: UserRole.Developer, label: L.Enum.UserRole('DEVELOPER') }
										]}
									/>
								)}
							/>
						</FormFieldGroup>
					) : null}
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
